/**
 * Version checking utility to ensure users always have the latest version
 */

// Import i18n instance for translations
import i18n from '../i18n';

// Declare global build version injected by Vite
declare const __BUILD_VERSION__: string;

// Get build version from Vite or fallback to timestamp
const BUILD_ID = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : Date.now().toString();

// Key for storing the current version in localStorage
const VERSION_KEY = 'piika_app_version';

/**
 * Check if user has the latest version and force refresh if needed
 */
export const checkAndForceRefresh = (): void => {
  try {
    // Get stored version and current version
    const storedVersion = localStorage.getItem(VERSION_KEY);
    const currentVersion = getBuildVersion();
    
    console.log('Version Check:', { storedVersion, currentVersion });
    
    // If no stored version, this is first visit - store current version
    if (!storedVersion) {
      localStorage.setItem(VERSION_KEY, currentVersion);
      return;
    }
    
    // If versions don't match, user has old version
    if (storedVersion !== currentVersion) {
      console.warn('Version mismatch detected! Showing update notification...');
      
      // Show update notification instead of force refresh
      showUpdateNotification();
    } else {
      // Versions match, no action needed
      console.log('Version check passed - user has latest version');
    }
  } catch (error) {
    console.error('Error in version check:', error);
  }
};

/**
 * Get current build version
 */
export const getBuildVersion = (): string => {
  // Try to get version from meta tag first (if set during build)
  const metaVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
  if (metaVersion) {
    return metaVersion;
  }
  
  // Fallback to build ID
  return BUILD_ID;
};

/**
 * Update stored version to current version
 */
export const updateStoredVersion = (): void => {
  const currentVersion = getBuildVersion();
  localStorage.setItem(VERSION_KEY, currentVersion);
};

/**
 * Clear all possible caches
 */
const clearAllCaches = async (): Promise<void> => {
  try {
    // Clear browser caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    
    // Clear localStorage items related to caching
    const cacheKeys = [
      'workbox-precache',
      'workbox-runtime',
      'vite-cache',
      'piika_cache_'
    ];
    
    cacheKeys.forEach(key => {
      // Remove exact matches and prefix matches
      Object.keys(localStorage).forEach(storageKey => {
        if (storageKey === key || storageKey.startsWith(key)) {
          localStorage.removeItem(storageKey);
        }
      });
    });
    
    console.log('All caches cleared successfully');
  } catch (error) {
    console.error('Error clearing caches:', error);
  }
};

/**
 * Show update notification to user
 */
const showUpdateNotification = (): void => {
  // Check if notification is already shown
  if (document.getElementById('version-update-notification')) {
    return;
  }

  // Create a non-intrusive notification banner
  const notification = document.createElement('div');
  notification.id = 'version-update-notification';
  notification.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 12px 20px;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    animation: slideDown 0.3s ease-out;
  `;

  // Add slide down animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideDown {
      from {
        transform: translateY(-100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
    @keyframes slideUp {
      from {
        transform: translateY(0);
        opacity: 1;
      }
      to {
        transform: translateY(-100%);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);

  const messageContainer = document.createElement('div');
  messageContainer.style.cssText = `
    display: flex;
    align-items: center;
    gap: 12px;
  `;

  const icon = document.createElement('span');
  icon.innerHTML = '🔄';
  icon.style.fontSize = '18px';

  const message = document.createElement('span');
  message.textContent = i18n.t('versionCheck.updateAvailable');

  messageContainer.appendChild(icon);
  messageContainer.appendChild(message);

  const buttonContainer = document.createElement('div');
  buttonContainer.style.cssText = `
    display: flex;
    align-items: center;
    gap: 8px;
  `;

  const refreshButton = document.createElement('button');
  refreshButton.textContent = i18n.t('versionCheck.refreshNow');
  refreshButton.style.cssText = `
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    transition: all 0.2s ease;
  `;

  refreshButton.addEventListener('mouseenter', () => {
    refreshButton.style.background = 'rgba(255, 255, 255, 0.3)';
  });

  refreshButton.addEventListener('mouseleave', () => {
    refreshButton.style.background = 'rgba(255, 255, 255, 0.2)';
  });

  refreshButton.addEventListener('click', () => {
    clearAllCaches().then(() => {
      window.location.reload();
    });
  });

  const dismissButton = document.createElement('button');
  dismissButton.textContent = i18n.t('versionCheck.later');
  dismissButton.style.cssText = `
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
    border: none;
    padding: 6px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s ease;
  `;

  dismissButton.addEventListener('mouseenter', () => {
    dismissButton.style.color = 'white';
    dismissButton.style.background = 'rgba(255, 255, 255, 0.1)';
  });

  dismissButton.addEventListener('mouseleave', () => {
    dismissButton.style.color = 'rgba(255, 255, 255, 0.8)';
    dismissButton.style.background = 'transparent';
  });

  dismissButton.addEventListener('click', () => {
    notification.style.animation = 'slideUp 0.3s ease-out';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 300);
    
    // Show again after 5 minutes if user dismissed
    setTimeout(() => {
      checkAndForceRefresh();
    }, 5 * 60 * 1000);
  });

  buttonContainer.appendChild(refreshButton);
  buttonContainer.appendChild(dismissButton);

  notification.appendChild(messageContainer);
  notification.appendChild(buttonContainer);

  document.body.appendChild(notification);

  // Auto-dismiss after 30 seconds if user doesn't interact
  setTimeout(() => {
    if (document.getElementById('version-update-notification')) {
      dismissButton.click();
    }
  }, 30000);
};

/**
 * Show manual refresh message to user (for critical situations)
 */
const showManualRefreshMessage = (): void => {
  // Create a modal-like overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;
  
  const message = document.createElement('div');
  message.style.cssText = `
    background: white;
    padding: 24px;
    border-radius: 8px;
    max-width: 400px;
    text-align: center;
    margin: 20px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  `;
  
  message.innerHTML = `
    <h3 style="margin: 0 0 16px 0; color: #1f2937;">${i18n.t('versionCheck.updateRequired')}</h3>
    <p style="margin: 0 0 20px 0; color: #6b7280; line-height: 1.5;">
      ${i18n.t('versionCheck.updateRequiredMessage')}
    </p>
    <button onclick="window.location.reload(true)" style="
      background: #3b82f6;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    ">${i18n.t('versionCheck.refreshPage')}</button>
  `;
  
  overlay.appendChild(message);
  document.body.appendChild(overlay);
};

/**
 * Initialize version checking
 */
export const initVersionCheck = (): void => {
  // Check version on load
  checkAndForceRefresh();
  
  // Update version after successful load
  updateStoredVersion();
  
  // Check for version changes when page becomes visible
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      checkAndForceRefresh();
    }
  });
  
  // Optional: Check periodically (every 5 minutes)
  setInterval(checkAndForceRefresh, 5 * 60 * 1000);
}; 